import { FC } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { BuilderTypes } from '@frontend/forms-builder-engine';
import { DragAndDrop } from '../../../../../constants';
import { useFormBuilderStore } from '../../../../../provider';
import { BuilderFieldDivisionTypes } from '../../../../../types';
import { DraggableUtils, DroppableUtils } from '../../../../../utils';
import { DraggableField, DraggableFieldUtils } from '../../draggable-field';
import { builderFieldsStyle } from './droppable-field-list.styles';

/**
 * This map is used to determine if a field is a primary medical history field.
 */
const PRIMARY_MEDICAL_HISTORY_FIELD_IDENTIFIER: Partial<
  Record<BuilderTypes.BuilderFormElementTypes.PrimaryFields.PrimaryFieldKey, boolean>
> = {
  allergies: true,
  medications: true,
  disease: true,
};

interface PrimaryFieldCategoryProps {
  category: BuilderFieldDivisionTypes.PrimaryFieldsDivisionCategory;
  fields: BuilderTypes.BuilderFormElementTypes.PrimaryFields.ParsedFields;
  type: 'primary';
  usedFields: BuilderTypes.BuilderFormElementTypes.PrimaryFields.UsedPrimaryFields;
}

interface PaymentsFieldCategoryProps {
  category: BuilderFieldDivisionTypes.PaymentsFieldsDivisionCategory;
  fields: BuilderTypes.BuilderFormElementTypes.PaymentsFields.ParsedFields;
  type: 'payments';
}

interface OtherFieldCategoryProps {
  category: BuilderFieldDivisionTypes.OtherFieldsDivisionCategory;
  fields: BuilderTypes.BuilderFormElementTypes.OtherFields.CombinedOtherFields;
  type: 'other';
}

type DropableFieldListProps = PrimaryFieldCategoryProps | PaymentsFieldCategoryProps | OtherFieldCategoryProps;

const DroppableFieldList: FC<DropableFieldListProps> = (props) => {
  const { category, fields, type } = props;
  const { medicalConditions } = useFormBuilderStore(['medicalConditions']);

  return (
    <Droppable
      droppableId={DroppableUtils.field.getFieldCategoryDroppableId(category.label)}
      isDropDisabled
      type={DragAndDrop.DroppableType.FIELD}
    >
      {(provided) => (
        <ul css={builderFieldsStyle} {...provided.droppableProps} ref={provided.innerRef}>
          {category.fieldsOrder.map((field, fieldOrderIndex) => {
            let displayName = '';
            let draggableId = '';
            let isAlreadyUsed = false;
            let isEmpty = false;
            const isSyncable = field.syncable || false;

            switch (type) {
              case 'primary': {
                const fieldKey = field.key as BuilderTypes.BuilderFormElementTypes.PrimaryFields.PrimaryFieldKey;
                const fieldData = fields[fieldKey];
                const dataKey = fieldData.meta.dataKey;
                displayName = fieldData.meta.displayName;
                draggableId = DraggableUtils.field.getPrimaryFieldDraggableId(fieldKey);
                isAlreadyUsed = !!props.usedFields[fieldKey];

                const isPrimaryMedicalHistoryField = PRIMARY_MEDICAL_HISTORY_FIELD_IDENTIFIER[dataKey] || false;
                if (isPrimaryMedicalHistoryField) {
                  const medicalHistoryOptions =
                    medicalConditions[dataKey as BuilderTypes.PrimaryMedicalHistoryFieldTypes.OptionSetType]?.optionSet
                      ?.options || [];
                  isEmpty = medicalHistoryOptions.length === 0;
                }
                break;
              }

              case 'payments': {
                const fieldKey = field.key as BuilderTypes.BuilderFormElementTypes.PaymentsFields.ParsedFieldKey;
                const fieldData = fields[fieldKey];
                displayName = fieldData.meta.displayName;
                draggableId = DraggableUtils.field.getPaymentsFieldDraggableId(fieldKey);
                break;
              }

              default: {
                const fieldKey = field.key as BuilderTypes.BuilderFormElementTypes.OtherFields.CombinedOtherFieldsKey;
                const fieldData = fields[fieldKey];
                displayName = fieldData.meta.displayName;
                draggableId = DraggableUtils.field.getOtherFieldDraggableId(fieldKey);
                break;
              }
            }

            return (
              <DraggableField
                displayName={displayName}
                index={fieldOrderIndex}
                draggableId={draggableId}
                key={field.key}
                appearance={DraggableFieldUtils.getDragabbleFieldAppearance({
                  isAlreadyUsed,
                  isSyncable,
                  isEmpty,
                })}
                trackingId={field.trackingId}
              />
            );
          })}
        </ul>
      )}
    </Droppable>
  );
};

export default DroppableFieldList;
